import React from "react";
import { useTranslation } from "react-i18next";
import { EmailBuilderSettingsCornerRoundingSection } from "../controls/EmailBuilderSettingsCornerRoundingSection";
import { EmailBuilderSettingsToggleField } from "../controls/EmailBuilderSettingsToggleField";
import { UrlField } from "../UrlField";
import { SettingSection } from "../controls/SettingSection";
import { ButtonAppearanceSection } from "../ButtonAppearanceSection";
import {
  baselineButtonRowSlotBorderRadius,
  baselineButtonRowSlotSize,
} from "../../domain/baselines";
import {
  isCornerRoundingInactive,
} from "../settingsSectionActivation";
import type { ButtonRowSlot } from "../../domain/types";

type ButtonRowSlotSettingsProps = {
  slot: ButtonRowSlot;
  slotIndex: number;
  patch: (next: Partial<ButtonRowSlot>) => void;
};

export function ButtonRowSlotSettings({
  slot,
  slotIndex,
  patch,
}: ButtonRowSlotSettingsProps) {
  const { t } = useTranslation();
  const defaultRadius = baselineButtonRowSlotBorderRadius(slotIndex);

  return (
    <>
      <SettingSection sectionId="content" title={t("Content")}>
        <UrlField
          label={t("Link")}
          value={slot.href}
          onChange={href => patch({ href })}
        />
      </SettingSection>

      <SettingSection sectionId="layout" title={t("Layout")}>
        <EmailBuilderSettingsToggleField
          label={t("Fill space")}
          checked={slot.fullWidth}
          onChange={fullWidth => patch({ fullWidth })}
        />
      </SettingSection>

      <SettingSection
        sectionId="corner-rounding"
        title={t("Corner rounding")}
        activation={{
          mode: "optional",
          isActive: !isCornerRoundingInactive(slot.borderRadius),
          onAdd: () =>
            patch({ borderRadius: { ...baselineButtonRowSlotBorderRadius(slotIndex) } }),
        }}
      >
        <EmailBuilderSettingsCornerRoundingSection
          value={slot.borderRadius ?? defaultRadius}
          onChange={borderRadius => {
            patch({
              borderRadius: {
                topLeft: borderRadius.topLeft,
                topRight: borderRadius.topRight,
                bottomRight: borderRadius.bottomRight,
                bottomLeft: borderRadius.bottomLeft,
              },
            });
          }}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <ButtonAppearanceSection
          value={slot}
          sizeDefault={baselineButtonRowSlotSize(slotIndex)}
          patch={patch}
        />
      </SettingSection>
    </>
  );
}
